import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { DxfLayout, ExtractedPageScale, Point2D } from '@paragon/contract-library';

import { VisibilityConfig, VisibilityConfiguration } from '../../library/hierarchy-filtering/visible-objects';
import {
  __UNSAFE_designItemsSelected,
  __UNSAFE_itemsDeselected,
  __UNSAFE_layoutItemsSelected,
  clearSelection,
} from './selectionsSlice';

function selectScaleForPage(extractedScales: ExtractedPageScale[], pageNumber: number): number | undefined {
  const scaleForChosenPage = extractedScales.find((scale) => scale.pageNumber === pageNumber);
  return scaleForChosenPage && scaleForChosenPage.scales.length > 0
    ? Math.max(...scaleForChosenPage.scales)
    : undefined;
}

interface SnappingUnderlayStatePendingBase {
  status: 'pending';

  previewPageNumber: number;
  chosenRotation: number;
}

export interface SnappingUnderlayStatePendingPdf extends SnappingUnderlayStatePendingBase {
  sourceType: 'pdf';

  pdfUrl: string;
  pdfFileName: string;

  extractedScales: ExtractedPageScale[];
  chosenScale?: number;
}

export interface SnappingUnderlayStatePendingDxf extends SnappingUnderlayStatePendingBase {
  sourceType: 'dxf';

  fileName: string;
  layouts: DxfLayout[];
}

type SnappingUnderlayStatePending = SnappingUnderlayStatePendingPdf | SnappingUnderlayStatePendingDxf;

type SnappingUnderlayUploadState =
  | { status: 'waiting' } //         nothing has happened; waiting on the user to upload a document
  | { status: 'loading' } //         document is currently being uploaded
  | { status: 'error' } //           document failed to upload
  | SnappingUnderlayStatePending; // document uploaded successfully; now waiting on the user to configure

export type ScalePickingState = {
  underlayGuid: string;
  basePoint?: Point2D;
  endPoint?: Point2D;
};

type SnappingUnderlayState = {
  upload: SnappingUnderlayUploadState;

  scalePicking?: ScalePickingState;

  visibleUnderlayConfiguration: VisibilityConfiguration;
};

const initialState: SnappingUnderlayState = {
  upload: { status: 'waiting' },
  visibleUnderlayConfiguration: { visibleObjects: [] },
};

export const snappingUnderlaySlice = createSlice({
  name: 'snappingUnderlay',
  initialState,
  reducers: {
    uploadBegan(state) {
      state.upload = { status: 'loading' };
    },

    uploadFailed(state) {
      state.upload = { status: 'error' };
    },

    pdfUploadCompleted(
      state,
      action: PayloadAction<{ pdfUrl: string; pdfFileName: string; extractedScales: ExtractedPageScale[] }>,
    ) {
      const previewPageNumber = 0;

      const { pdfUrl, pdfFileName, extractedScales } = action.payload;

      const chosenScale = selectScaleForPage(extractedScales, previewPageNumber);

      state.upload = castDraft({
        status: 'pending',
        sourceType: 'pdf',
        pdfUrl,
        pdfFileName,
        previewPageNumber,
        extractedScales,
        chosenScale,
        chosenRotation: 0,
      });
    },

    dxfUploadCompleted(state, action: PayloadAction<{ fileName: string; layouts: DxfLayout[] }>) {
      const { fileName, layouts } = action.payload;

      state.upload = castDraft({
        status: 'pending',
        sourceType: 'dxf',
        fileName,
        layouts,
        previewPageNumber: 0,
        chosenRotation: 0,
      });
    },

    previewPageChosen(state, action: PayloadAction<number>) {
      if (state.upload.status !== 'pending') throw new Error('invalid state');

      state.upload.previewPageNumber = action.payload;

      if (state.upload.sourceType === 'pdf')
        state.upload.chosenScale = selectScaleForPage(state.upload.extractedScales, action.payload);
    },

    scaleChosen(state, action: PayloadAction<number>) {
      if (state.upload.status !== 'pending' || state.upload.sourceType !== 'pdf') throw new Error('invalid state');

      state.upload.chosenScale = action.payload;
    },

    rotationChosen(state, action: PayloadAction<number>) {
      if (state.upload.status !== 'pending') throw new Error('invalid state');

      state.upload.chosenRotation = action.payload;
    },

    scalePickingStarted(state, action: PayloadAction<string>) {
      state.scalePicking = { underlayGuid: action.payload };
    },

    scalePickingBasePointSet(state, action: PayloadAction<{ x: number; y: number }>) {
      if (state.scalePicking) state.scalePicking.basePoint = action.payload;
    },

    scalePickingEndPointSet(state, action: PayloadAction<{ x: number; y: number }>) {
      if (state.scalePicking) state.scalePicking.endPoint = action.payload;
    },

    scalePickingCleared(state) {
      delete state.scalePicking;
    },

    setUnderlayVisibility(state, action: PayloadAction<{ guid: string; visible: boolean }>) {
      const { guid, visible } = action.payload;
      state.visibleUnderlayConfiguration = visible
        ? VisibilityConfig.add(state.visibleUnderlayConfiguration, guid)
        : VisibilityConfig.remove(state.visibleUnderlayConfiguration, guid);
    },

    showOnlyUnderlay(state, action: PayloadAction<string>) {
      state.visibleUnderlayConfiguration = VisibilityConfig.only(action.payload);
    },

    resetUnderlayVisibility(state) {
      state.visibleUnderlayConfiguration = 'all';
    },

    entirelyUnloaded(state) {
      state.upload = { status: 'waiting' };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(__UNSAFE_layoutItemsSelected, (state, action) => {
      delete state.scalePicking;

      if (action.payload.type === 'snappingUnderlay') {
        state.visibleUnderlayConfiguration = { visibleObjects: [...action.payload.itemGuids] };
      }
    });
    for (const action of [__UNSAFE_designItemsSelected, __UNSAFE_itemsDeselected, clearSelection] as const) {
      builder.addCase(action, (state) => {
        delete state.scalePicking;
      });
    }
  },
});
